import {useCallback, useMemo, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {useAllAdaptiveSubjects} from 'client/TeacherDashboard/Home/hooks/useHomeAdaptiveSubjects';
import type {SortValue} from 'client/TeacherDashboard/types';
import {normalizeError} from 'lib/normalizeError';

import type {SkillPathEntryFieldsFragment} from '../queries/SkillPaths.queries.generated';
import {useSkillPathEntriesQuery} from '../queries/SkillPaths.queries.generated';
import type {
  SkillPathRowData,
  SkillPathSortValue,
  SkillPathStatusSegment,
  SkillPathSubjectOption
} from '../types';
import type {SharedSkillPathFilters} from './skillPathEntryFilters';
import {
  STATUS_ENUM,
  buildEntryFilter,
  buildEntrySort,
  toClassroomIds
} from './skillPathEntryFilters';

const PAGE_SIZE = 25;

/** Stable identities, so an empty result doesn't read as a new one to consumers. */
const NO_ROWS: SkillPathRowData[] = [];
const NO_ENTRIES: SkillPathEntryFieldsFragment[] = [];

export interface SkillPathsListFilters extends SharedSkillPathFilters {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  sort: SortValue<SkillPathSortValue>;
}

interface Result {
  rows: SkillPathRowData[];
  subjectOptions: SkillPathSubjectOption[];
  isLoading: boolean;
  hasError: boolean;
  hasNextPage: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

const toRow = (
  entry: SkillPathEntryFieldsFragment,
  segment: SkillPathStatusSegment
): SkillPathRowData => ({
  id: entry.id,
  skillName: entry.name,
  code: entry.code ?? '',
  guideId: entry.guideId,
  subjectId: entry.subjectId,
  // `parent` is the topic, its own parent the theme.
  topicId: entry.parent?.id,
  themeId: entry.parent?.parent?.id,
  onStudentsSkillPaths: entry.onStudentsSkillPaths ?? false,
  // `buildEntryFilter` always pins `skillPathStatus` to the segment, so every row in this result
  // set holds it by construction — asking the server to roll it up per row only recomputes a value
  // the WHERE clause already guarantees. `skillPathEntryFilters.test.ts` pins that invariant.
  status: segment,
  minStartDate: entry.skillPathMinStartDate ?? null,
  maxStartDate: entry.skillPathMaxStartDate ?? null,
  minDueDate: entry.skillPathMinDueDate ?? null,
  maxDueDate: entry.skillPathMaxDueDate ?? null,
  studentCount: entry.skillPathStudentCount,
  completedStudentCount: entry.skillPathCompletedStudentCount
});

export const useSkillPathsList = ({
  classroomId,
  segment,
  sort,
  ...shared
}: SkillPathsListFilters): Result => {
  const {search, subject, dueWithin, dueDateStart, dueDateEnd} = shared;

  const variables = useMemo(
    () => ({
      classroomIds: toClassroomIds(classroomId),
      status: STATUS_ENUM[segment],
      // Selects which date pair the row fragment asks for.
      isScheduled: segment === 'scheduled',
      filter: buildEntryFilter({search, subject, dueWithin, dueDateStart, dueDateEnd}, segment),
      sort: buildEntrySort(sort, segment),
      limit: PAGE_SIZE
    }),
    [classroomId, segment, sort, search, subject, dueWithin, dueDateStart, dueDateEnd]
  );

  const {data, loading, error, refetch, fetchMore} = useSkillPathEntriesQuery({
    variables,
    fetchPolicy: 'cache-and-network'
  });

  // Every async pagination write is keyed to the request that produced it, and reads ignore any
  // key but the current one — so a response landing after a filter change (or a refetch, via the
  // generation) is inert by construction rather than filtered by a guard that could race.
  const [refetchGen, setRefetchGen] = useState(0);
  const variablesKey = `${refetchGen}:${JSON.stringify(variables)}`;
  // Pages past the first accumulate here: the Apollo field is argument-keyed, so it cannot merge
  // them itself without a typePolicy.
  const [morePages, setMorePages] = useState({key: '', entries: NO_ENTRIES});
  // The watched query only ever holds page one, whose `hasNextPage` stays true; the page that says
  // there is nothing left is the last one `fetchMore` fetched.
  const [exhaustedKey, setExhaustedKey] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // A variables change starts a new result set; clear the paging flag so the new list doesn't
  // render a "loading more" that isn't happening, blocking its own first `loadMore`.
  const [renderedVariablesKey, setRenderedVariablesKey] = useState(variablesKey);
  if (renderedVariablesKey !== variablesKey) {
    setRenderedVariablesKey(variablesKey);
    if (isLoadingMore) {
      setIsLoadingMore(false);
    }
  }

  const moreEntries = morePages.key === variablesKey ? morePages.entries : NO_ENTRIES;

  const page = data?.skillPathEntries;
  const firstPageResults = page?.results;
  const entries = useMemo(
    () => [...(firstPageResults ?? []), ...moreEntries],
    [firstPageResults, moreEntries]
  );

  const rows = useMemo(
    () => (entries.length > 0 ? entries.map((entry) => toRow(entry, segment)) : NO_ROWS),
    [entries, segment]
  );

  // The subject filter lists the whole adaptive catalog, not only subjects the teacher has paths in.
  const {subjects: adaptiveSubjects} = useAllAdaptiveSubjects();
  const subjectOptions = useMemo<SkillPathSubjectOption[]>(
    () => adaptiveSubjects.map((s) => ({id: s.id, name: s.name})),
    [adaptiveSubjects]
  );

  const hasNextPage = exhaustedKey !== variablesKey && Boolean(page?.hasNextPage);

  const loadMore = useCallback(() => {
    if (loading || isLoadingMore || !hasNextPage) {
      return;
    }
    const requestedKey = variablesKey;
    setIsLoadingMore(true);
    fetchMore({variables: {...variables, offset: entries.length}})
      .then((result) => {
        const nextPage = result.data?.skillPathEntries;
        if (nextPage?.hasNextPage === false) {
          setExhaustedKey(requestedKey);
        }
        const next = nextPage?.results ?? [];
        // De-duplicated: an edit between pages shifts offsets and can repeat a row.
        setMorePages((current) => {
          const base = current.key === requestedKey ? current.entries : NO_ENTRIES;
          const seen = new Set([
            ...entries.map((entry) => entry.id),
            ...base.map((entry) => entry.id)
          ]);
          return {key: requestedKey, entries: [...base, ...next.filter((e) => !seen.has(e.id))]};
        });
      })
      .catch((fetchMoreError) => {
        // Toast rather than blank the list — the rows already on screen are still good.
        addToast({
          color: 'negative',
          title: 'Couldn’t load more skill paths',
          message: normalizeError(fetchMoreError).message
        });
      })
      .finally(() => setIsLoadingMore(false));
  }, [entries, fetchMore, hasNextPage, isLoadingMore, loading, variables, variablesKey]);

  const handleRefetch = useCallback(() => {
    // Bumping the generation retires every key: accumulated pages stop being read and any in-flight
    // terminal page can no longer mark the fresh result set exhausted.
    setRefetchGen((generation) => generation + 1);
    // Swallow the rejection: the failure is surfaced reactively via `error`.
    void refetch().catch(() => {});
  }, [refetch]);

  return {
    rows,
    subjectOptions,
    isLoading: loading && !data,
    hasError: Boolean(error),
    hasNextPage,
    isLoadingMore,
    loadMore,
    refetch: handleRefetch
  };
};
